/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

export interface GodavariUserAgentInfo {
  browserName: string;
  browserVersion: string;
  osName: string;
  osVersion: string;
  deviceModel: string;
  manufacturer: string;
}

type OperatingSystemInfo = Pick<GodavariUserAgentInfo, 'osName' | 'osVersion' | 'deviceModel' | 'manufacturer'>;

const BROWSERS: Array<[string, RegExp]> = [
  ['Edge', /Edg(?:e|A|iOS)?\/([\d.]+)/],
  ['Opera', /(?:OPR|Opera)\/([\d.]+)/],
  ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
  ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/],
  ['Safari', /Version\/([\d.]+) Safari/],
];

/** Parses the browser, OS and device details SonyLIV reports (e.g. Chrome / macOS / Macintosh / Apple). */
export function parseGodavariUserAgent(userAgent: string | undefined): GodavariUserAgentInfo | undefined {
  if (!userAgent) {
    return undefined;
  }
  const browser = BROWSERS.find(([, pattern]) => pattern.exec(userAgent) !== null);
  const browserMatch = browser?.[1].exec(userAgent);
  const browserVersion = browserMatch?.[1] ?? 'NA';
  const operatingSystem = parseOperatingSystem(userAgent);

  return {
    browserName: browser ? browser[0] : 'NA',
    browserVersion,
    ...operatingSystem,
  };
}

function parseOperatingSystem(userAgent: string): OperatingSystemInfo {
  const macOsMatch = /Mac OS X ([\d_.]+)/.exec(userAgent);
  const isAppleMobile = /iPhone|iPad/.exec(userAgent) !== null;
  if (macOsMatch && !isAppleMobile) {
    return {
      osName: 'macOS',
      osVersion: macOsMatch[1].replace(/_/g, '.'),
      deviceModel: 'Macintosh',
      manufacturer: 'Apple',
    };
  }

  const iosMatch = /(?:iPhone|CPU) OS ([\d_]+)/.exec(userAgent);
  if (iosMatch) {
    return {
      osName: 'iOS',
      osVersion: iosMatch[1].replace(/_/g, '.'),
      deviceModel: /iPad/.exec(userAgent) ? 'iPad' : 'iPhone',
      manufacturer: 'Apple',
    };
  }

  const androidMatch = /Android ([\d.]+)/.exec(userAgent);
  if (androidMatch) return { osName: 'Android', osVersion: androidMatch[1], deviceModel: 'NA', manufacturer: 'NA' };

  const windowsMatch = /Windows NT ([\d.]+)/.exec(userAgent);
  if (windowsMatch) return { osName: 'Windows', osVersion: windowsMatch[1], deviceModel: 'NA', manufacturer: 'NA' };

  if (/CrOS/.exec(userAgent)) return { osName: 'Chrome OS', osVersion: 'NA', deviceModel: 'NA', manufacturer: 'NA' };
  if (/Linux/.exec(userAgent)) return { osName: 'Linux', osVersion: 'NA', deviceModel: 'NA', manufacturer: 'NA' };

  return { osName: 'NA', osVersion: 'NA', deviceModel: 'NA', manufacturer: 'NA' };
}

export function getGodavariUserAgent(): string | undefined {
  return typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
}
